interface BackendErrorResponse {
    readonly message?: unknown;
    readonly details?: unknown;
}

export type RoomKickForbiddenReason = 'ROOM_CREATOR' | 'ROOM_ADMINISTRATOR';

export class RoomKickError extends Error {
    constructor(
        readonly status: number,
        message: string,
        readonly reason?: RoomKickForbiddenReason,
    ) {
        super(message);
        this.name = 'RoomKickError';
    }
}

export const createRoomKickError = (
    status: number,
    statusText: string,
    responseBody: string,
): RoomKickError => {
    const backendError = parseBackendError(responseBody);
    const message = backendError?.message ?? (statusText || 'Room kick failed');
    return new RoomKickError(status, message, backendError?.reason);
};

export const isRoomKickForbidden = (error: unknown): boolean =>
    error instanceof RoomKickError && error.status === 403;

export const getRoomKickForbiddenDescription = (error: unknown): string => {
    if (!(error instanceof RoomKickError)) return 'You do not have permission to remove this member from the room.';

    switch (error.reason) {
        case 'ROOM_CREATOR':
            return 'You cannot remove the room creator.';
        case 'ROOM_ADMINISTRATOR':
            return 'You cannot remove a room administrator.';
        default:
            return 'You do not have permission to remove this member from the room.';
    }
};

const parseBackendError = (
    responseBody: string,
): {readonly message: string | null; readonly reason?: RoomKickForbiddenReason} | null => {
    if (!responseBody) return null;

    try {
        const parsedBody: unknown = JSON.parse(responseBody);
        if (!isBackendErrorResponse(parsedBody)) return null;

        const message = typeof parsedBody.message === 'string' ? parsedBody.message.trim() || null : null;
        return {message, reason: parseForbiddenReason(parsedBody.details)};
    } catch {
        return null;
    }
};

const parseForbiddenReason = (details: unknown): RoomKickForbiddenReason | undefined => {
    if (typeof details !== 'object' || details === null || !('reason' in details)) return undefined;
    const reason = details.reason;
    return reason === 'ROOM_CREATOR' || reason === 'ROOM_ADMINISTRATOR' ? reason : undefined;
};

const isBackendErrorResponse = (value: unknown): value is BackendErrorResponse =>
    typeof value === 'object' && value !== null;
